import React from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Divider,
    Box,
    Collapse,
    Badge,
    Theme,
} from "@mui/material";
import {
    // Sales
    PointOfSale,
    ShoppingBag,
    Group,
    Storefront,
    QuestionAnswer,
    TaskAlt,
    // Catalog
    Pets,
    Category,
    Label,
    Inventory,
    // Inventory
    Warehouse,
    LocalShipping,
    Business,
    RequestQuote,
    // Finance
    ReceiptLong,
    AccountBalance,
    AccountBalanceWallet,
    Payments,
    Analytics,
    TrendingUp,
    Insights,
    // System
    Dashboard,
    Construction,
    Tune,
    AdminPanelSettings,
    // UI
    ExpandLess,
    ExpandMore,
    ListAlt,
    MergeType,
    ViewQuilt,
    Campaign,
    CallSplit,
    Email,
    Map,
    SyncAlt,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { NavItem } from "../../types";
import { useAdminStats } from "../../hooks/useAdminStats";
import { NotificationCounts } from "../../hooks/useNotificationCounts";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useAnswerDeskCount } from "../../hooks/useAnswerDeskCount";

const drawerWidth = 280;

// ── Section-based nav structure ───────────────────────────────────────────────

interface NavSection {
    label: string;
    items: NavItem[];
}

const BASE_SECTIONS: NavSection[] = [
    {
        label: "Sales",
        items: [
            {
                id: "pos",
                label: "Point of Sale",
                path: "/pos",
                icon: PointOfSale,
            },
            {
                id: "orders",
                label: "Orders",
                path: "/orders",
                icon: ShoppingBag,
                children: [
                    { id: "orders-list", label: "All Orders", path: "/orders", icon: ListAlt },
                    { id: "orders-requests", label: "Online Requests", path: "/orders/requests", icon: Storefront, badgeKey: "pendingOrderRequests" },
                ],
            },
            {
                id: "customers",
                label: "Customers",
                path: "/customers",
                icon: Group,
                children: [
                    { id: "customers-list", label: "All Customers", path: "/customers", icon: Group },
                    { id: "customers-messages", label: "Contact Messages", path: "/customers/messages", icon: Email, badgeKey: "unreadContactMessages" },
                ],
            },
            {
                id: "answer-desk",
                label: "Answer Desk",
                path: "/answers",
                icon: QuestionAnswer,
            },
            {
                id: "tasks",
                label: "Tasks",
                path: "/tasks",
                icon: TaskAlt,
            },
        ],
    },
    {
        label: "Catalog",
        items: [
            {
                id: "products",
                label: "Products",
                path: "/products",
                icon: Pets,
                children: [
                    { id: "products-list", label: "All Products", path: "/products", icon: Inventory },
                    { id: "products-arrange", label: "Arrange Shop", path: "/products/arrange", icon: ViewQuilt },
                    { id: "campaigns", label: "Campaigns", path: "/campaigns", icon: Campaign },
                    { id: "products-merge", label: "Merge Products", path: "/products/merge", icon: MergeType },
                    { id: "categories", label: "Categories", path: "/categories", icon: Category },
                    { id: "brands", label: "Brands", path: "/brands", icon: Label },
                ],
            },
        ],
    },
    {
        label: "Inventory",
        items: [
            {
                id: "stock",
                label: "Stock",
                path: "/inventory/stock",
                icon: Warehouse,
            },
            {
                id: "procurement",
                label: "Procurement",
                path: "/procurement/purchases",
                icon: LocalShipping,
                children: [
                    { id: "procurement-suppliers", label: "Suppliers", path: "/procurement/suppliers", icon: Business },
                    { id: "procurement-purchases", label: "Purchase Orders", path: "/procurement/purchases", icon: RequestQuote },
                ],
            },
            {
                id: "conversions",
                label: "Conversions",
                path: "/inventory/conversions",
                icon: SyncAlt,
            },
        ],
    },
    {
        label: "Finance",
        items: [
            {
                id: "expenses",
                label: "Expenses",
                path: "/expenses",
                icon: ReceiptLong,
            },
            {
                id: "assets",
                label: "Assets",
                path: "/assets",
                icon: AccountBalance,
            },
            {
                id: "accounts",
                label: "Accounts",
                path: "/accounts",
                icon: AccountBalanceWallet,
            },
            {
                id: "payments",
                label: "Payments",
                path: "/payments",
                icon: Payments,
            },
            {
                id: "reports",
                label: "Reports",
                path: "/reports/profit-loss",
                icon: Analytics,
                children: [
                    { id: "reports-pl", label: "Profit & Loss", path: "/reports/profit-loss", icon: TrendingUp },
                    { id: "reports-shop", label: "Shop Analytics", path: "/reports/shop-analytics", icon: Insights },
                ],
            },
        ],
    },
    {
        label: "System",
        items: [
            {
                id: "dashboard",
                label: "Dashboard",
                path: "/dashboard",
                icon: Dashboard,
            },
            {
                id: "tools",
                label: "Tools",
                path: "/tools",
                icon: Construction,
            },
            {
                id: "settings",
                label: "Settings",
                path: "/settings",
                icon: Tune,
                children: [
                    { id: "settings-dashboard", label: "Dashboard Settings", path: "/settings", icon: Tune },
                    { id: "settings-ecommerce", label: "E-commerce Website", path: "/settings?tab=1", icon: Storefront },
                    { id: "delivery-services", label: "Delivery Services", path: "/settings/delivery-services", icon: LocalShipping },
                    { id: "delivery-areas", label: "Delivery Areas", path: "/settings/delivery-areas", icon: Map },
                    { id: "order-channels", label: "Order Channels", path: "/settings/order-channels", icon: CallSplit },
                    { id: "expense-categories", label: "Expense Categories", path: "/settings/expense-categories", icon: ReceiptLong },
                    { id: "settings-accounting", label: "Accounting Setup", path: "/settings?tab=2", icon: AccountBalance },
                ],
            },
        ],
    },
];

// Nav items (and specific children) that live entirely in ResourceArea.OPERATIONS
// on the backend — an Investor login can't reach any of these, so they're
// stripped from the nav rather than left to 404/403 on click.
const INVESTOR_HIDDEN_ITEM_IDS = new Set([
    "pos", "procurement", "expenses", "assets", "tools", "settings", "answer-desk", "tasks",
]);
const INVESTOR_HIDDEN_CHILD_IDS = new Set([
    "orders-requests", // guest order requests — OPERATIONS
    "customers-messages", // contact messages — OPERATIONS
    "products-merge", // write-only workflow, nothing to browse read-only
]);

// ── Component ─────────────────────────────────────────────────────────────────

interface SidebarProps {
    open: boolean;
    onClose?: () => void;
    isMobile?: boolean;
    notificationCounts: NotificationCounts;
}

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose, isMobile = false, notificationCounts }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedItems, setExpandedItems] = React.useState<string[]>(["orders", "products"]);
    const { user } = useAuth();
    const { isInvestor } = usePermissions();
    const { stats } = useAdminStats(30000, user?.role === "OWNER");
    // Fetched once in Layout and shared with Header's bell — see useNotificationCounts.
    const { pendingOrderRequests, unreadContactMessages, taskSummary } = notificationCounts;

    const isOwner = user?.role === "OWNER";
    // Answer Desk work waiting for Owner/Manager approval.
    const answerDeskWaiting = useAnswerDeskCount(isOwner || user?.role === "MANAGER");

    // Build sections — add Owner-only items to System section, strip
    // OPERATIONS-only items/children an Investor login can't reach.
    const sections = React.useMemo<NavSection[]>(() => {
        let result = BASE_SECTIONS;

        if (isInvestor) {
            result = result
                .map((section) => ({
                    ...section,
                    items: section.items
                        .filter((item) => !INVESTOR_HIDDEN_ITEM_IDS.has(item.id))
                        .map((item) => item.children
                            ? { ...item, children: item.children.filter((c) => !INVESTOR_HIDDEN_CHILD_IDS.has(c.id)) }
                            : item
                        ),
                }))
                .filter((section) => section.items.length > 0);
        }

        if (!isOwner) return result;
        return result.map((section) => {
            if (section.label !== "System") return section;
            return {
                ...section,
                items: [
                    ...section.items,
                    { id: "user-approval", label: "Staff Accounts", path: "/admin/user-approval", icon: AdminPanelSettings },
                ],
            };
        });
    }, [isOwner, isInvestor]);

    const handleItemClick = (item: NavItem) => {
        if (item.children) {
            setExpandedItems((prev) =>
                prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
            );
        } else {
            navigate(item.path);
            if (isMobile && onClose) onClose();
        }
    };

    // The menu item for this page: the one whose path matches it most
    // closely, so /products/arrange lights up "Arrange Shop" and not
    // "All Products" (/products) as well.
    const activePath = React.useMemo(() => {
        const paths: string[] = [];
        const collect = (items: NavItem[]) =>
            items.forEach((i) => {
                paths.push(i.path);
                if (i.children) collect(i.children);
            });
        sections.forEach((section) => collect(section.items));
        return paths
            .filter((p) => location.pathname === p || location.pathname.startsWith(p + "/"))
            .sort((a, b) => b.length - a.length)[0];
    }, [sections, location.pathname]);
    const isItemActive = (path: string) => path === activePath;

    const renderNavItem = (item: NavItem, level = 0) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItems.includes(item.id);
        const isActive = isItemActive(item.path);

        const iconNode = (() => {
            if ((item.id === "user-approval" || item.id === "admin") && stats?.pending_users) {
                return <Badge badgeContent={stats.pending_users} color="error"><item.icon /></Badge>;
            }
            if (item.id === "orders-requests" && pendingOrderRequests > 0) {
                return <Badge badgeContent={pendingOrderRequests} color="warning"><item.icon /></Badge>;
            }
            if (item.id === "customers-messages" && unreadContactMessages > 0) {
                return <Badge badgeContent={unreadContactMessages} color="warning"><item.icon /></Badge>;
            }
            if (item.id === "answer-desk" && answerDeskWaiting > 0) {
                return <Badge badgeContent={answerDeskWaiting} color="warning"><item.icon /></Badge>;
            }
            // My open tasks — red when something's overdue, amber when due today.
            if (item.id === "tasks" && taskSummary && taskSummary.open_assigned > 0) {
                const color = taskSummary.overdue > 0 ? "error" : taskSummary.due_today > 0 ? "warning" : "info";
                return <Badge badgeContent={taskSummary.open_assigned} color={color}><item.icon /></Badge>;
            }
            return <item.icon />;
        })();

        return (
            <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ pl: level * 2 }}>
                    <ListItemButton
                        onClick={() => handleItemClick(item)}
                        selected={isActive && !hasChildren}
                        sx={{
                            minHeight: 44,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                            "&.Mui-selected": {
                                backgroundColor: "primary.main",
                                color: "primary.contrastText",
                                "&:hover": { backgroundColor: "primary.dark" },
                                "& .MuiListItemIcon-root": { color: "primary.contrastText" },
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : "auto", justifyContent: "center" }}>
                            {iconNode}
                        </ListItemIcon>
                        <ListItemText primary={item.label} sx={{ opacity: open ? 1 : 0 }} />
                        {hasChildren && open && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                </ListItem>
                {hasChildren && (
                    <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {item.children!.map((child) => renderNavItem(child, level + 1))}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    // Collapsed on desktop: a narrow icon rail. The drawer itself and its
    // panel must share one width — the drawer is what takes room in the
    // page's row, so a wider drawer than panel pushes the page sideways.
    const collapsed = !isMobile && !open;
    const width = collapsed
        ? {
              width: (theme: Theme) => theme.spacing(7),
              [`@media (min-width: ${600}px)`]: { width: (theme: Theme) => theme.spacing(9) },
          }
        : { width: drawerWidth };
    const widthTransition = (theme: Theme) =>
        theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        });

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? open : true}
            onClose={onClose}
            sx={{
                ...width,
                flexShrink: 0,
                transition: widthTransition,
                "& .MuiDrawer-paper": {
                    ...width,
                    boxSizing: "border-box",
                    transition: widthTransition,
                },
            }}
        >
            <Toolbar>
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Typography variant="h6" noWrap component="div" sx={{ opacity: open ? 1 : 0 }}>
                        Pet Shop Admin
                    </Typography>
                </Box>
            </Toolbar>
            <Divider />

            <Box sx={{ overflowY: "auto", flex: 1 }}>
                {sections.map((section, sIdx) => (
                    <React.Fragment key={section.label}>
                        {/* Section label — only visible when drawer is open */}
                        {open && (
                            <Typography
                                variant="caption"
                                fontWeight={700}
                                letterSpacing={1}
                                color="text.disabled"
                                sx={{ px: 2.5, pt: sIdx === 0 ? 1.5 : 1, pb: 0.25, display: "block", textTransform: "uppercase" }}
                            >
                                {section.label}
                            </Typography>
                        )}
                        {!open && sIdx > 0 && <Divider sx={{ my: 0.5 }} />}
                        <List disablePadding>
                            {section.items.map((item) => renderNavItem(item))}
                        </List>
                    </React.Fragment>
                ))}
            </Box>
        </Drawer>
    );
};
